/*----------------------------------
- OPEN GRAPH BANNERS (SSOT)
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { EDITION_PAGES, resolveEditionPersonaFromPath } from '@uniquedomains/components/contracts/editions/public';
import { SALES_PATH } from '@uniquedomains/components/contracts/sales/public';
import { AUCTIONS_PATH } from '@uniquedomains/components/contracts/auctions/public';
import { REGISTRARS_PATH } from '@uniquedomains/components/contracts/registrars/public';
import { AVAILABILITY_PATH, WHOIS_PATH } from '@uniquedomains/components/contracts/lookup/limits';
import { AGENDA_PATH, TLD_HUB_PATH } from '@uniquedomains/core/domains/tlds';

/*----------------------------------
- TYPES
----------------------------------*/

export type WebsiteOpenGraphBanner = {
    alt: string;
    filename: string;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

export const WEBSITE_OPEN_GRAPH_PUBLIC_DIRECTORY = '/public/og';

export const DEFAULT_WEBSITE_OPEN_GRAPH_BANNER: WebsiteOpenGraphBanner = {
    alt: 'Unique Domains one-word domain decision platform',
    filename: 'master-1200x630.png',
};

/**
 * The Domaining News archive banner, also the fallback of the news share card. Exported so the
 * news route and its public resources point at this one file instead of a hardcoded path.
 */
export const DOMAINING_NEWS_OPEN_GRAPH_BANNER: WebsiteOpenGraphBanner = {
    alt: 'Daily Domaining News from Unique Domains',
    filename: 'og-domaining-news.png',
};

/**
 * Both daily editions pages (ADR-0074) share one object, like the market pages share
 * `DOMAINS_OPEN_GRAPH_BANNER`: the published list dedupes by identity, so two literals naming one
 * file would publish it twice.
 */
const EDITIONS_OPEN_GRAPH_BANNER: WebsiteOpenGraphBanner = {
    alt: 'Trending one-word domains and business ideas, a new edition every day',
    filename: 'og-opportunities.png',
};

export const DOMAINS_OPEN_GRAPH_BANNER: WebsiteOpenGraphBanner = {
    alt: 'Explore one-word domains with Unique Domains',
    filename: 'og-domains-hub.png',
};

export const WEBSITE_OPEN_GRAPH_ROUTE_BANNERS: Readonly<Record<string, WebsiteOpenGraphBanner>> = {
    '/': DEFAULT_WEBSITE_OPEN_GRAPH_BANNER,
    [AGENDA_PATH]: { alt: 'Every TLD launch and domain event, dated', filename: 'og-agenda.png' },
    '/api': { alt: 'Unique Domains API documentation', filename: 'og-api.png' },
    '/compare': { alt: 'Compare Unique Domains with other domain tools', filename: 'og-compare-hub.png' },
    '/compare/atom': { alt: 'Unique Domains compared with Atom.com', filename: 'og-vs-atom.png' },
    '/compare/bishopi': { alt: 'Unique Domains compared with Bishopi', filename: 'og-vs-bishopi.png' },
    '/compare/brandbucket': {
        alt: 'Unique Domains compared with BrandBucket',
        filename: 'og-vs-brandbucket.png',
    },
    '/compare/dotdb': { alt: 'Unique Domains compared with dotDB', filename: 'og-vs-dotdb.png' },
    '/compare/estibot': { alt: 'Unique Domains compared with Estibot', filename: 'og-vs-estibot.png' },
    '/compare/expireddomains-net': {
        alt: 'Unique Domains compared with ExpiredDomains.net',
        filename: 'og-vs-expireddomains-net.png',
    },
    '/compare/instant-domain-search': {
        alt: 'Unique Domains compared with Instant Domain Search',
        filename: 'og-vs-instant-domain-search.png',
    },
    '/compare/mononames': { alt: 'Unique Domains compared with mononames', filename: 'og-vs-mononames.png' },
    '/compare/namebio': { alt: 'Unique Domains compared with NameBio', filename: 'og-vs-namebio.png' },
    '/compare/one-word-domains': {
        alt: 'Unique Domains compared with oneword.domains',
        filename: 'og-vs-one-word-domains.png',
    },
    '/domains': DOMAINS_OPEN_GRAPH_BANNER,
    [TLD_HUB_PATH]: { alt: 'Every domain ending, one page each', filename: 'og-tld.png' },
    [SALES_PATH]: { alt: 'The newest one-word domain sales', filename: 'og-sales.png' },
    [AUCTIONS_PATH]: { alt: 'One-word domain auctions closing today', filename: 'og-auctions.png' },
    [REGISTRARS_PATH]: { alt: 'Registrar prices for every domain ending', filename: 'og-registrars.png' },
    /*
     * The lookup tools (ADR-0078): their result pages wear the tool's banner through the prefix
     * fallback below, never an image naming the looked-up name.
     */
    [WHOIS_PATH]: { alt: 'Look up who owns a domain name', filename: 'og-whois.png' },
    [AVAILABILITY_PATH]: { alt: 'Check if a domain name is free and what it costs', filename: 'og-availability.png' },
    '/glossary': { alt: 'Domain words defined in plain English', filename: 'og-glossary.png' },
    '/newsletter': { alt: 'The domain market in ten minutes a week', filename: 'og-newsletter.png' },
    '/pricing': { alt: 'Unique Domains plans and pricing', filename: 'og-pricing.png' },
    '/resources/domaining-news': DOMAINING_NEWS_OPEN_GRAPH_BANNER,
    '/product/appraise': { alt: 'Appraise domains with every source at once', filename: 'og-appraise.png' },
    // The daily editions (ADR-0074) keep the trends banner until a dated edition's own card
    // (`/<page>/<date>/share.png`) replaces it on that edition's page.
    [EDITION_PAGES.investor.path]: EDITIONS_OPEN_GRAPH_BANNER,
    [EDITION_PAGES.founder.path]: EDITIONS_OPEN_GRAPH_BANNER,
    '/product/radars': { alt: 'Track fresh domain matches with Radars', filename: 'og-radars.png' },
    '/product/search': { alt: 'Search one-word domains with Unique Domains', filename: 'og-search.png' },
    '/product/verify': { alt: 'Verify a domain before buying it', filename: 'og-verify.png' },
    '/product/watchlists': { alt: 'Track domain changes with Watchlists', filename: 'og-watchlists.png' },
    '/solutions/founders': { alt: 'Unique Domains for founders', filename: 'og-founders.png' },
    '/solutions/investors': { alt: 'Unique Domains for domain investors', filename: 'og-investors.png' },
    '/team': { alt: 'About Unique Domains', filename: 'og-team.png' },
};

export const WEBSITE_OPEN_GRAPH_RESERVED_BANNERS: readonly WebsiteOpenGraphBanner[] = [
    { alt: 'Unique Domains investor relations', filename: 'og-investor-relations.png' },
];

export const WEBSITE_OPEN_GRAPH_PUBLISHED_BANNERS: readonly WebsiteOpenGraphBanner[] = [
    ...new Set([...Object.values(WEBSITE_OPEN_GRAPH_ROUTE_BANNERS), ...WEBSITE_OPEN_GRAPH_RESERVED_BANNERS]),
];

/*----------------------------------
- HELPERS
----------------------------------*/

const normalizePathname = (requestUrl: string): string => {
    const pathname = new URL(requestUrl, 'https://unique.domains').pathname.replace(/\/+$/, '');
    return pathname || '/';
};

export const websiteOpenGraphBannerPublicPath = (banner: WebsiteOpenGraphBanner): string => {
    return `${WEBSITE_OPEN_GRAPH_PUBLIC_DIRECTORY}/${banner.filename}`;
};

export const resolveWebsiteOpenGraphBanner = (requestUrl: string): WebsiteOpenGraphBanner => {
    const pathname = normalizePathname(requestUrl);
    const exactBanner = WEBSITE_OPEN_GRAPH_ROUTE_BANNERS[pathname];
    if (exactBanner) return exactBanner;

    if (pathname.startsWith('/api/')) return WEBSITE_OPEN_GRAPH_ROUTE_BANNERS['/api'];
    if (pathname.startsWith('/compare/')) return WEBSITE_OPEN_GRAPH_ROUTE_BANNERS['/compare'];
    if (pathname.startsWith('/domains/')) return WEBSITE_OPEN_GRAPH_ROUTE_BANNERS['/domains'];
    if (pathname.startsWith(`${WHOIS_PATH}/`)) return WEBSITE_OPEN_GRAPH_ROUTE_BANNERS[WHOIS_PATH];
    if (pathname.startsWith(`${AVAILABILITY_PATH}/`)) return WEBSITE_OPEN_GRAPH_ROUTE_BANNERS[AVAILABILITY_PATH];
    // The insight pages and the applications list wear the TLD hub's banner.
    if (pathname.startsWith(`${TLD_HUB_PATH}/`)) return WEBSITE_OPEN_GRAPH_ROUTE_BANNERS[TLD_HUB_PATH];
    if (pathname.startsWith(`${REGISTRARS_PATH}/`)) return WEBSITE_OPEN_GRAPH_ROUTE_BANNERS[REGISTRARS_PATH];
    // Every page of the editions (a trend, a month) wears its page's banner.
    const editionPersona = resolveEditionPersonaFromPath(pathname);
    if (editionPersona) return WEBSITE_OPEN_GRAPH_ROUTE_BANNERS[EDITION_PAGES[editionPersona].path];
    if (pathname.startsWith('/resources/domaining-news/')) {
        return WEBSITE_OPEN_GRAPH_ROUTE_BANNERS['/resources/domaining-news'];
    }

    return DEFAULT_WEBSITE_OPEN_GRAPH_BANNER;
};
